import { BotIcon, StarIcon } from "lucide-react";

import { ActivityEventRow, ActivityQuote, EventIconCircle, UserName } from "./ActivityEventRow";

import { RichTextDisplay } from "@/components";

import { useGetUser } from "@/hooks/queries/useGetUser";
import { agenticBucketToLabel, agenticScoreToBucket } from "@/lib/agenticScore";
import { activityTypography } from "@/lib/constants";
import type { CandidacyAgenticScoreChange } from "@/types";

// Agent-generated when userId is null; a human staffer override otherwise.
export const AgenticScoreChangeEvent = ({
  currentUserId,
  scoreChange,
}: {
  currentUserId: number;
  scoreChange: CandidacyAgenticScoreChange;
}) => {
  /* Database CRUD Operations */
  // Read
  const isHuman = scoreChange.userId != null;
  // Agent rows have no user to resolve.
  const { data: user } = useGetUser({
    enabled: isHuman,
    userId: scoreChange.userId ?? 0,
  });

  /* Helpers */
  const fromBucket = agenticScoreToBucket(scoreChange.fromScore);
  const toBucket = agenticScoreToBucket(scoreChange.toScore);
  const fromLabel = fromBucket ? agenticBucketToLabel[fromBucket] : null;
  const toLabel = toBucket ? agenticBucketToLabel[toBucket] : "";

  /* Render */
  const renderSentence = () => {
    // Agent rows have no actor to name, so they read passively.
    if (!isHuman) {
      return (
        <>
          Agentic rating was set to{" "}
          <span className={activityTypography.emphasis}>{toLabel}</span>
          {fromLabel && fromLabel !== toLabel ? (
            <>
              {" "}
              from{" "}
              <span className={activityTypography.emphasis}>{fromLabel}</span>
            </>
          ) : null}
          <BotIcon className="ml-1 inline h-3.5 w-3.5 align-text-bottom text-purple-600" />
        </>
      );
    }

    return (
      <>
        <UserName currentUserId={currentUserId} user={user} />{" "}
        {fromLabel && fromLabel !== toLabel ? (
          <>
            changed the agentic rating from{" "}
            <span className={activityTypography.emphasis}>{fromLabel}</span> to{" "}
            <span className={activityTypography.emphasis}>{toLabel}</span>
          </>
        ) : (
          <>
            set the agentic rating to{" "}
            <span className={activityTypography.emphasis}>{toLabel}</span>
          </>
        )}
      </>
    );
  };

  const render = () => {
    return (
      <ActivityEventRow
        createdAt={scoreChange.createdAt}
        icon={
          <EventIconCircle className="border border-primary-900">
            <StarIcon className="h-4 w-4 text-primary-900" />
          </EventIconCircle>
        }
        quote={
          // Agent justifications can be very long; only show human overrides.
          isHuman && scoreChange.justification ? (
            <ActivityQuote className="mt-2">
              <RichTextDisplay richText={scoreChange.justification} />
            </ActivityQuote>
          ) : null
        }
      >
        {renderSentence()}
      </ActivityEventRow>
    );
  };

  return render();
};
